// src/auth/identiteCompte.ts — SCE Issues
// Qui est derrière le compte MSAL connecté.
//
// Presque toujours : le compte lui-même. Sauf pour un compte secondaire déclaré
// dans config/comptesAlias.config (téléphone perso d'un salarié) : c'est alors
// la personne interne qu'il représente.
//
// Un seul endroit pour le décider. useCurrentUser (droits, préférences, Mes
// déclarations), `nomReel` (textes écrits) et la présence le lisent : trois
// lectures séparées du compte auraient fini par diverger — un alias reconnu
// pour les droits mais signalé sous un autre nom.
//
// Compte PARTAGÉ (iPad de l'atelier, 07/10) : la personne choisie sur
// l'appareil (auth/comptePartage). Pas encore choisie → null, comme sans
// compte : aucun droit, aucune présence, tant que la question n'a pas eu de
// réponse (GardeComptePartage la pose avant tout le reste).
//
// ⚠️ À ne pas confondre avec la bascule de rôle (Lot 7), qui AFFICHE un autre
//    profil que la personne connectée. Ici, c'est la personne connectée
//    elle-même, simplement sous un autre compte.

import type { AccountInfo } from '@azure/msal-browser'
import {
  comptePartage,
  principalDuCompte,
  type CompteAlias,
  type ComptePartage,
} from '@/config/comptesAlias.config'
import { personneChoisie } from '@/auth/comptePartage'

export interface IdentiteCompte {
  upn: string
  email: string
  /** Brut, tel que l'annuaire ou la table l'écrit — passer par nomComplet(). */
  nomBrut: string
}

/** Object ID Entra du compte : claim `oid`, sinon l'identifiant local MSAL. */
export function objectIdDuCompte(account: AccountInfo): string {
  const claims = (account.idTokenClaims ?? {}) as Record<string, unknown>
  return typeof claims['oid'] === 'string' ? claims['oid'] : account.localAccountId
}

/** Le compte partagé derrière ce compte MSAL, ou null (cas ordinaire). */
export function partageDuCompte(
  account: AccountInfo | null,
  partages?: readonly ComptePartage[],
): ComptePartage | null {
  return account ? comptePartage(objectIdDuCompte(account), partages) : null
}

export interface OptionsIdentite {
  partages?: readonly ComptePartage[]
  /** UPN choisi sur l'appareil ; par défaut, celui retenu (auth/comptePartage). */
  choisi?: string | null
}

export function identiteDuCompte(
  account: AccountInfo | null,
  table?: readonly CompteAlias[],
  options: OptionsIdentite = {},
): IdentiteCompte | null {
  if (!account) return null

  const claims = (account.idTokenClaims ?? {}) as Record<string, unknown>
  const oid = objectIdDuCompte(account)

  const principal = principalDuCompte(oid, table)
  if (principal) {
    return { upn: principal.upn, email: principal.email, nomBrut: principal.nom }
  }

  const partage = comptePartage(oid, options.partages)
  if (partage) {
    const choisi = (options.choisi !== undefined ? options.choisi : personneChoisie(partage.objectId)) ?? ''
    const personne = partage.personnes.find((p) => p.upn.toLowerCase() === choisi.toLowerCase())
    return personne ? { upn: personne.upn, email: personne.email, nomBrut: personne.nom } : null
  }

  const upn = account.username ?? ''
  const emailClaim = typeof claims['email'] === 'string' ? claims['email'] : ''
  return { upn, email: emailClaim || upn, nomBrut: account.name ?? upn }
}
